/**
 * Feature Flag Context
 * Provides feature flags throughout the app
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import featureFlagService from '../services/featureFlagService';

const FeatureFlagContext = createContext({
  features: {},
  customLimits: {},
  hasFeature: () => false,
  getLimit: () => null,
  isLoading: true,
  reload: async () => {},
  applyOnboardingAnswers: () => {}
});

export function FeatureFlagProvider({ children, user }) {
  const [features, setFeatures] = useState({});
  const [customLimits, setCustomLimits] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only fetch features if user is authenticated
    if (user?.id) {
      loadFeatures();
    } else {
      // No user - clear features
      setFeatures({});
      setCustomLimits({});
      setIsLoading(false);
    }
  }, [user?.id]);

  const loadFeatures = async () => {
    setIsLoading(true);

    try {
      const { features: loadedFeatures, customLimits: loadedLimits } =
        await featureFlagService.fetchUserFeatures();

      setFeatures(loadedFeatures);
      setCustomLimits(loadedLimits);
    } catch (error) {
      console.error('[FeatureFlags] Error loading features:', error);
      setFeatures({});
      setCustomLimits({});
    } finally {
      setIsLoading(false);
    }
  };

  const hasFeature = (featureKey) => {
    return features[featureKey] === true;
  };

  const getLimit = (limitKey, defaultValue = null) => {
    return customLimits[limitKey] ?? defaultValue;
  };

  const reload = async () => {
    await loadFeatures();
  };

  // Applies onboarding answers synchronously (no server round-trip), then
  // reconciles with the server in the background. The caller's own
  // completeOnboarding() call already persisted these same answers -- this
  // just avoids showing everything as disabled while that reconcile fetch
  // is still in flight.
  const applyOnboardingAnswers = (answers) => {
    const { features: seededFeatures, customLimits: seededLimits } =
      featureFlagService.seedFromOnboarding(answers);
    setFeatures(seededFeatures);
    setCustomLimits(seededLimits);
    setIsLoading(false);
    loadFeatures();
  };

  const contextValue = {
    features,
    customLimits,
    hasFeature,
    getLimit,
    isLoading,
    reload,
    applyOnboardingAnswers
  };

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Hook to access feature flags in components
 *
 * @example
 * function MyComponent() {
 *   const { hasFeature, getLimit } = useFeatureFlags();
 *
 *   if (!hasFeature('ai_chat')) {
 *     return null; // Feature not enabled
 *   }
 *
 *   const maxTokens = getLimit('maxTokens', 1000);
 *
 *   return <AIChatComponent maxTokens={maxTokens} />;
 * }
 */
export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    console.warn('[FeatureFlags] useFeatureFlags must be used within FeatureFlagProvider');
    // Return safe defaults
    return {
      features: {},
      customLimits: {},
      hasFeature: () => false,
      getLimit: () => null,
      isLoading: false,
      reload: async () => {},
      applyOnboardingAnswers: () => {}
    };
  }

  return context;
}

/**
 * HOC to conditionally render component based on feature flag
 *
 * @example
 * const AIChatButton = withFeatureFlag(ChatButton, 'ai_chat');
 *
 * // Only renders if 'ai_chat' feature is enabled
 * <AIChatButton />
 */
export function withFeatureFlag(Component, featureKey, FallbackComponent = null) {
  return function FeatureFlaggedComponent(props) {
    const { hasFeature, isLoading } = useFeatureFlags();

    if (isLoading) {
      return null; // Or a loading skeleton
    }

    if (!hasFeature(featureKey)) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }

    return <Component {...props} />;
  };
}

/**
 * Component to conditionally render children based on feature flag
 *
 * @example
 * <FeatureFlag feature="ai_chat">
 *   <AIChatButton />
 * </FeatureFlag>
 *
 * // Or with fallback
 * <FeatureFlag feature="ai_chat" fallback={<UpgradePrompt />}>
 *   <AIChatButton />
 * </FeatureFlag>
 */
export function FeatureFlag({ feature, fallback = null, children }) {
  const { hasFeature, isLoading } = useFeatureFlags();

  if (isLoading) {
    return null; // Or a loading skeleton
  }

  if (!hasFeature(feature)) {
    return fallback;
  }

  return <>{children}</>;
}

export default FeatureFlagContext;
